<template>
	<div class="brand-logo-overrides-page">
		<admin-table
			ref="overridesTable"
			title="Brand Logo Overrides"
			icon="🖼️"
			:columns="columns"
			:data="overrides"
			:loading="loading"
			:searchable="true"
			search-placeholder="Search overrides..."
			:collapsible="false"
			:default-expanded="true"
			:editable="true"
			:selectable="false"
			:deletable="true"
			:addable="true"
			empty-message="No overrides yet. Add one below."
			@row-edit-save="handleEditSave"
			@row-delete="handleDelete"
			@row-add="handleAdd"
		>
			<template #cell-preview="{ row, editingRowId }">
				<template v-if="editingRowId === row.id">
					<v-progress-circular v-if="previewPending" indeterminate size="24" width="2"></v-progress-circular>
					<img
						v-else-if="previewSrc"
						:src="previewSrc"
						class="logo-preview"
						@error="$event.target.src = require('@/assets/round3.png')"
					/>
				</template>
				<img
					v-else-if="row.override"
					:src="'https://api.sysodev.link/BrandLogos/' + _cleanBrandName(row.override).replace(/[ /]/g, '+')"
					class="logo-preview"
					@error="$event.target.src = require('@/assets/round3.png')"
				/>
			</template>
			<template #add-cell-preview>
				<v-progress-circular v-if="previewPending" indeterminate size="24" width="2"></v-progress-circular>
				<img
					v-else-if="previewSrc"
					:src="previewSrc"
					class="logo-preview"
					@error="$event.target.src = require('@/assets/round3.png')"
				/>
			</template>
		</admin-table>
	</div>

	<!-- Delete Confirmation Dialog -->
	<MGDialog
		v-model="showDeleteDialog"
		title="Delete Override"
		max-width="300px"
		:persistent="true"
	>
		<template #content>
			<p>Are you sure you want to delete this override?</p>
		</template>
		<template #actions>
			<MGButton text="Delete" @click="confirmDelete" :stacked="true" />
			<MGButton text="Cancel" :outline="true" @click="showDeleteDialog = false" :stacked="true" />
		</template>
	</MGDialog>

	<!-- Validation Toast -->
	<v-snackbar
		v-model="showValidationToast"
		:timeout="4000"
		location="bottom"
		color="error"
	>
		{{ validationMessage }}
		<template #actions>
			<v-btn
				icon="mdi-close"
				size="small"
				@click="showValidationToast = false"
			></v-btn>
		</template>
	</v-snackbar>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminTable from '@/admin/components/AdminTable.vue';
import MGDialog from '@/components/elements/MGDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';

export default {
	name: 'BrandLogoOverridesPage',
	components: { AdminTable, MGDialog, MGButton },
	mixins: [AdminServices],
	data() {
		return {
			loading: false,
			overrides: [],
			showDeleteDialog: false,
			deleteRow: null,
			showValidationToast: false,
			validationMessage: '',
			previewSrc: null,
			previewPending: false,
			columns: [
				{
					field: 'searchRegEx',
					label: 'Search Regex',
					width: '2fr',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., ^AMAZON.*'
				},
				{
					field: 'override',
					label: 'Override',
					width: '2fr',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., Amazon'
				},
				{
					field: 'preview',
					label: 'Preview',
					width: '80px',
					sortable: false,
					editable: false
				}
			]
		};
	},
	methods: {
		onOverrideInput(val) {
			console.log('[BrandLogoPreview] onOverrideInput called with:', val);
			this.previewSrc = null;
			this.previewPending = !!val;
			clearTimeout(this._previewTimer);
			if (val) {
				this._previewTimer = setTimeout(() => {
					const cleanedName = this._cleanBrandName(val);
					const url = 'https://api.sysodev.link/BrandLogos/' + cleanedName.replace(/[ /]/g, '+');
					console.log('[BrandLogoPreview] cleanedName:', cleanedName);
					console.log('[BrandLogoPreview] previewSrc URL:', url);
					this.previewSrc = url;
					this.previewPending = false;
				}, 1000);
			}
		},
		loadOverrides() {
			this.loading = true;
			this._getLogoSearchOverrides((data) => {
				this.overrides = data;
				this.loading = false;
			});
		},
		handleAdd(data) {
			if (!data.searchRegEx || !data.override) {
				const missing = [];
				if (!data.searchRegEx) missing.push('Search Regex');
				if (!data.override) missing.push('Override');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}
			this._createLogoSearchOverride(
				{ searchRegEx: data.searchRegEx, override: data.override.replace(/\//g, '') },
				() => {
					this.loadOverrides();
					this.$refs.overridesTable?.resetAddRow();
				},
				(err) => {
					this.validationMessage = err;
					this.showValidationToast = true;
				}
			);
		},
		handleEditSave({ rowId, data }) {
			if (!data.searchRegEx || !data.override) {
				const missing = [];
				if (!data.searchRegEx) missing.push('Search Regex');
				if (!data.override) missing.push('Override');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}
			this._updateLogoSearchOverride(rowId,
				{ searchRegEx: data.searchRegEx, override: data.override.replace(/\//g, '') },
				() => {
					this.$refs.overridesTable?.cancelEdit();
					this.loadOverrides();
				},
				(err) => {
					this.validationMessage = err;
					this.showValidationToast = true;
				}
			);
		},
		handleDelete(row) {
			this.deleteRow = row;
			this.showDeleteDialog = true;
		},
		confirmDelete() {
			if (!this.deleteRow) return;
			this._deleteLogoSearchOverride(this.deleteRow.id,
				() => {
					this.overrides = this.overrides.filter(o => o.id !== this.deleteRow.id);
					this.deleteRow = null;
					this.showDeleteDialog = false;
				},
				(err) => {
					this.validationMessage = err;
					this.showValidationToast = true;
					this.showDeleteDialog = false;
				}
			);
		}
	},
	mounted() {
		this.loadOverrides();
		this.$nextTick(() => {
			const table = this.$refs.overridesTable;
			if (table) {
				this.$watch(() => table.editingData?.override, (val) => this.onOverrideInput(val));
				this.$watch(() => table.addRowData?.override, (val) => this.onOverrideInput(val));
			}
		});
	}
};
</script>

<style scoped>
.brand-logo-overrides-page {
	min-height: 100%;
	padding: 15px;
	overflow-x: hidden;
}

.override-cell {
	display: flex;
	align-items: center;
	gap: 8px;
}

.logo-preview {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background-color: #E7ECF7;
	flex-shrink: 0;
}

.cell-value {
	color: var(--admin-text-primary);
	font-size: 14px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}

:root.light-mode .brand-logo-overrides-page {
	background-color: transparent;
}
</style>
